// src/composables/api/ticketing/useTicketingEventsApi.ts
import { useAuthHttp } from '../useAuthHttp';
import type { AxiosRequestConfig } from 'axios';

type AnyResponse = any;

export type DuplicateEventPayload = {
  name: string;
  slug?: string | null;
  event_id?: number | string | null;
  event_start_date: string; // YYYY-MM-DD
  event_end_date: string;   // YYYY-MM-DD
  start_time_24h?: string | null; // HH:mm
  end_time_24h?: string | null;   // HH:mm
  timezone?: string | null;
  copy_tickets?: boolean;
  copy_discounts?: boolean;
  copy_faqs?: boolean;
  copy_images?: boolean;
};

export function useTicketingEventsApi() {
  const { get, post, put, del, patch } = useAuthHttp();

  // POST /api/ticketing/events
  const createEvent = async (formData: object, config: AxiosRequestConfig = {}): Promise<AnyResponse> => {
    return await post('/ticketing/events', formData, config);
  };

  // GET /api/ticketing/events
  const listEvents = async (config: AxiosRequestConfig = {}): Promise<AnyResponse> => {
    return await get('/ticketing/events', config);
  };

  // GET /api/ticketing/events/:id
  const getEvent = async (eventId: string | number, config: AxiosRequestConfig = {}): Promise<AnyResponse> => {
    return await get(`/ticketing/events/${eventId}`, config);
  };

  // GET /api/ticketing/events/:id/dashboard
  const getEventDashboard = (eventId: string | number, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    get(`/ticketing/events/${eventId}/dashboard`, config);  

  // PUT /api/ticketing/events/:id
  const updateEvent = async (
    eventId: string | number,
    formData: object,
    config: AxiosRequestConfig = {}
  ): Promise<AnyResponse> => {
    return await post(`/ticketing/events/${eventId}`, formData, config);
  };

  // DELETE /api/ticketing/events/:id
  const deleteEvent = async (eventId: string | number, config: AxiosRequestConfig = {}): Promise<AnyResponse> => {
    return await del(`/ticketing/events/${eventId}`, config);
  };

  const updateEventTax = (eventId: string | number, payload: object, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    patch(`/ticketing/events/${eventId}/tax`, payload, config)

  const updateEventFees = (eventId: string | number, payload: object, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    patch(`/ticketing/events/${eventId}/fees`, payload, config)

  const updateEventConfirmations = (eventId: string | number, payload: object, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    patch(`/ticketing/events/${eventId}/confirmations`, payload, config)

  const updateEventDetails = (eventId: string | number, payload: object, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    patch(`/ticketing/events/${eventId}/event-details`, payload, config)

  const updateDoorSales = (eventId: string | number, payload: { show_door_sales: boolean; door_sales_message: string }, config: AxiosRequestConfig = {}): Promise<AnyResponse> =>
    patch(`/ticketing/events/${eventId}/door-sales`, payload, config)

const updateEventMedia = (
    eventId: string | number,
    formData: FormData,
    config: AxiosRequestConfig = {}
  ) => post(`/ticketing/events/${eventId}/media`, formData, config);

  // POST /api/ticketing/events/:id/duplicate
  // Copies an event (groups, tickets, discounts, FAQs, images, per-day times — never orders)
  // into a new Draft. Response: { data: EventResource, meta: { copied, skipped, day_offset, ... } }
  const duplicateEvent = async (
    eventId: string | number,
    payload: DuplicateEventPayload,
    config: AxiosRequestConfig = {}
  ): Promise<AnyResponse> => {
    return await post(`/ticketing/events/${eventId}/duplicate`, payload, config);
  };

  // GET /api/ticketing/events/:id/gallery
  const getGalleryImages = async (
    eventId: string | number,
    groupId?: number | null,
    config: AxiosRequestConfig = {}
  ): Promise<AnyResponse> => {
    const params = groupId !== undefined && groupId !== null ? { group_id: groupId } : {};
    return await get(`/ticketing/events/${eventId}/gallery`, { ...config, params });
  };

  // POST /api/ticketing/events/:id/gallery
  const updateGalleryImages = async (
    eventId: string | number,
    formData: FormData,
    config: AxiosRequestConfig = {}
  ): Promise<AnyResponse> => {
    return await post(`/ticketing/events/${eventId}/gallery`, formData, config);
  };

  return {
    createEvent,
    listEvents,
    getEvent,
    getEventDashboard,
    updateEvent,
    deleteEvent,
    updateEventTax,
    updateEventFees,
    updateEventConfirmations,
    updateEventMedia,
    updateEventDetails,
    updateDoorSales,
    duplicateEvent,
    getGalleryImages,
    updateGalleryImages
  };
}